import React, { useState } from 'react';
import { cx } from './Primitives';
import { IconChevronRight } from './icons';

export interface DisclosureProps {
    title: string;
    /** What is inside, in a few words, so a closed section still answers the question. */
    summary?: React.ReactNode;
    defaultOpen?: boolean;
    icon?: React.ReactNode;
    /** Something to do without opening: "Add", "Send". */
    action?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
}

/**
 * A section that is one line until it is needed.
 *
 * The follow-up dialog used to be one long scroll: contacts, the WhatsApp
 * picker, cheques and account settings all open at once above the one thing
 * a call ends with — the outcome and the next date. Everything secondary now
 * folds to its summary line, and a person who needs it opens it.
 */
export const Disclosure = ({ title, summary, defaultOpen = false, icon, action, className, children }: DisclosureProps) => {
    const [open, setOpen] = useState(defaultOpen);
    // A row between two hairlines, not a box (2026-10 refinement): it usually sits inside a surface already, and a
    // box inside a card is the nesting the product avoids. Several in a list share their lines (`-mt-px` on each
    // after the first, where the list stacks them).
    return (
        <section className={cx('border-y border-separator', className)}>
            <div className="flex items-center gap-3 min-h-12">
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    aria-expanded={open}
                    className="flex-1 min-w-0 flex items-center gap-2.5 py-3 text-left"
                >
                    <span className={cx('flex-none text-label-3 transition-transform', open && 'rotate-90')} aria-hidden="true">
                        <IconChevronRight />
                    </span>
                    {icon && <span className="flex-none text-label-2" aria-hidden="true">{icon}</span>}
                    <span className="text-body font-semibold text-label">{title}</span>
                    {/* The summary takes what room is left and no more (zero basis, no minimum): a long one is cut short with an
                        ellipsis instead of widening the page — inside a grid its whole width would otherwise count as the minimum. */}
                    {summary && !open && <>{' '}<span className="flex-1 w-0 min-w-0 text-secondary text-label-3 truncate">{summary}</span></>}
                </button>
                {action && <div className="flex-none">{action}</div>}
            </div>
            {open && <div className="pl-7 pb-4 pt-1">{children}</div>}
        </section>
    );
};

